import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { useAuth } from '../context/AppContext'
import { Plus, Pencil, Trash2, ShieldAlert, CheckCircle2, XCircle, Search, RefreshCw, Layers, Webhook, ArrowRight, ArrowLeft, Check, Layers3, Eye, Copy } from 'lucide-react'
import { ENGINE_OPTIONS, ENGINE_LABELS } from '../utils/buyerEngine'

const BUYERS_RESPONSIVE_CSS = `
@media (max-width: 768px) {
  .partners-hero {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 16px !important;
  }
  .partners-toolbar-actions { width: 100% !important; flex-wrap: wrap !important; }
  .partners-toolbar-actions .btn { flex: 1 1 auto !important; justify-content: center !important; }

  .partners-stats-grid { grid-template-columns: 1fr 1fr !important; }
  .partners-grid { grid-template-columns: 1fr !important; }

  .partners-filters { flex-direction: column !important; align-items: stretch !important; }
  .partners-filters .search-bar,
  .partners-filters .select,
  .partners-filters .btn { width: 100% !important; }

  .partner-modal, .partner-modal-wide {
    width: 100% !important;
    max-width: 100% !important;
  }
  .partner-modal-grid-2 { grid-template-columns: 1fr !important; }
  .partner-modal-actions { flex-wrap: wrap !important; }
  .partner-modal-actions .btn { flex: 1 1 auto !important; justify-content: center !important; }

  .partner-modal .input,
  .partner-modal .select,
  .partners-filters .input { font-size: 16px !important; }
}

.wizard-steps {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: var(--bg-raised);
  border-bottom: 1px solid var(--border);
  gap: 8px;
}
.wizard-step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
  flex: 1;
}
.wizard-step-item.active {
  color: var(--accent);
}
.wizard-step-item.completed {
  color: var(--green);
}
.step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--bg-surface);
  border: 1px solid var(--border-strong);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}
.wizard-step-item.active .step-number {
  background: var(--accent);
  color: white;
  border-color: var(--accent);
}
.wizard-step-item.completed .step-number {
  background: var(--green);
  color: white;
  border-color: var(--green);
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
}
.mapping-table th {
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 6px 8px;
  border-bottom: 1px solid var(--border);
}
.mapping-table td {
  padding: 6px 8px;
}
`

// Parses one field_mapping entry from the API into the row shape the
// Advanced Field & Value Mapping editor uses. Shared by the initial form
// state and the edit-mode fetch, since both need to turn the same
// backend shape (engine/senders.py::map_payload's rule format) into rows.
// Infers which Type dropdown option a raw JSON value (a value_map "to", or
// a rule's "default") should show as, from its actual JSON type. Anything
// that isn't a real bool/number falls back to 'string' - including the
// "not set" case, so a fresh row defaults sensibly.
function inferValueType(value) {
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  return 'string'
}

// Coerces a raw text-input string into the JSON type its Type dropdown says
// it should be, for both value_map "to" values and rule "default" values -
// the two places a config author enters a value the backend needs typed
// correctly (e.g. EPCVIP's income_type: 2, home_owner: true), not left as
// whatever a plain text input always produces (a string). A non-numeric
// "number" input falls back to the raw trimmed string rather than sending
// NaN/null, since that's silently wrong in a different way.
function coerceTypedValue(raw, type) {
  const trimmed = raw.trim()
  if (type === 'boolean') return trimmed === 'true'
  if (type === 'number') {
    const num = Number(trimmed)
    return Number.isNaN(num) ? trimmed : num
  }
  return trimmed
}

function parseFieldMappingEntry(internal, rule) {
  if (typeof rule !== 'object' || rule === null) {
    return { internal, target: String(rule), defaultValue: '', defaultType: 'string', ruleType: 'simple', castType: 'string', valueMap: [], buckets: [], dateFormat: '', concatFields: [], separator: '', baseDateField: '', payFrequencyField: '', cycles: '' }
  }

  // The backend accepts non-string default/value_map/bucket-label values
  // (e.g. PLL's DirectDeposit maps to integers 1/0, not "1"/"0") - every
  // row field here is a controlled text input, so every value coming out
  // of the backend has to be coerced to a string at this boundary or a
  // later .trim() on a raw number blows up. `!= null` (not `||`) so a
  // legitimate falsy default like 0 doesn't silently disappear. defaultType
  // remembers the real JSON type so save doesn't silently downgrade an
  // existing integer/boolean default back to a string.
  const base = {
    internal,
    target: rule.target || '',
    defaultValue: rule.default != null ? String(rule.default) : '',
    defaultType: inferValueType(rule.default),
    castType: 'string'
  }

  // A dict rule carrying only "target" (and optionally "cast") is still a
  // plain rename as far as map_payload is concerned - the backend rejects
  // "cast" combined with value_map/buckets/date_format/rule/now outright
  // (engine.senders.map_payload), so reaching this check with none of
  // those keys present means it's a Simple Rename that forces its output
  // type, not a real value_map/buckets/etc rule with nothing configured
  // in it yet.
  if (!rule.now && rule.rule == null && !rule.date_format && !rule.value_map && !Array.isArray(rule.buckets)) {
    return {
      ...base,
      ruleType: 'simple',
      castType: rule.cast || 'string',
      valueMap: [],
      buckets: [],
      dateFormat: '',
      concatFields: [],
      separator: '',
      baseDateField: '',
      payFrequencyField: '',
      cycles: ''
    }
  }

  if (Array.isArray(rule.buckets)) {
    return {
      ...base,
      ruleType: 'buckets',
      valueMap: [],
      dateFormat: '',
      concatFields: [],
      separator: '',
      baseDateField: '',
      payFrequencyField: '',
      cycles: '',
      buckets: rule.buckets.map(([upperBound, label]) => ({ upperBound: String(upperBound), label: String(label) }))
    }
  }

  // Must be checked before the plain `date_format` branch below - a "now"
  // rule (e.g. PLL's `datetime`) carries both `now: true` and `date_format`
  // (the now-value's output format), and doesn't read `internal` from a
  // real lead field at all. Checking date_format first would silently drop
  // the `now` flag and turn this into a dead rule reading a field that
  // never exists on any lead.
  if (rule.now) {
    return { ...base, ruleType: 'now', valueMap: [], buckets: [], dateFormat: rule.date_format || '', concatFields: [], separator: '', baseDateField: '', payFrequencyField: '', cycles: '' }
  }

  // "concat" doesn't read `internal` from a real lead field either (same
  // as "now") - it joins whatever's in `rule.fields` instead, which are
  // usually the synthetic __affiliate_id/__click_subid keys
  // routing/dispatch.py merges in at send time, not payload fields.
  if (rule.rule === 'concat') {
    return {
      ...base,
      ruleType: 'concat',
      valueMap: [],
      buckets: [],
      dateFormat: '',
      baseDateField: '',
      payFrequencyField: '',
      cycles: '',
      concatFields: Array.isArray(rule.fields) ? rule.fields.map(f => String(f)) : [],
      separator: rule.separator != null ? String(rule.separator) : ''
    }
  }

  // Must be checked before the plain `date_format` branch below - same
  // reason as "concat" above. A pay_cycle_offset rule (e.g. TruePath's
  // next_pay_date2, EPC's pay_after_next_date, PLL's second pay date)
  // carries its own `date_format` for the *output*, on top of `rule`,
  // `base_date_field`, and `pay_frequency_field` - checking date_format
  // first silently drops all three of those and leaves a dead rule that
  // reads `internal` (a synthetic key like __next_pay_date2, which is
  // never a real lead field) instead of computing anything. This exact
  // failure mode took every TruePath bid down (missing next_pay_date2)
  // until it was caught by diffing the live endpoint against the seed
  // script - see engine/senders.py::map_payload's "pay_cycle_offset" rule
  // in the backend repo.
  if (rule.rule === 'pay_cycle_offset') {
    return {
      ...base,
      ruleType: 'pay_cycle_offset',
      valueMap: [],
      buckets: [],
      concatFields: [],
      separator: '',
      dateFormat: rule.date_format || '',
      baseDateField: rule.base_date_field || '',
      payFrequencyField: rule.pay_frequency_field || '',
      cycles: rule.cycles != null ? String(rule.cycles) : ''
    }
  }

  if (rule.date_format) {
    return { ...base, ruleType: 'date_format', valueMap: [], buckets: [], dateFormat: rule.date_format, concatFields: [], separator: '', baseDateField: '', payFrequencyField: '', cycles: '' }
  }

  return {
    ...base,
    ruleType: 'value_map',
    buckets: [],
    dateFormat: '',
    concatFields: [],
    separator: '',
    baseDateField: '',
    payFrequencyField: '',
    // toType reflects the JSON type actually stored on the backend (e.g.
    // PLL's DirectDeposit uses real integers 1/0, not "1"/"0") - a row
    // loaded from an existing buyer keeps its real type on save rather
    // than getting silently downgraded to a string the moment someone
    // opens the edit wizard.
    valueMap: rule.value_map
      ? Object.entries(rule.value_map).map(([vk, vv]) => ({ from: vk, to: String(vv), toType: inferValueType(vv) }))
      : []
  }
}

// Fallback shown if GET /config/verticals/{vertical}/schema can't be
// reached (permission denied, offline, unknown vertical on old data) -
// keeps the Advanced Field & Value Mapping dropdown usable rather than
// empty, at the cost of not reflecting recently-added canonical fields.
const FALLBACK_INTERNAL_FIELDS = [
  'first_name', 'last_name', 'email', 'phone', 'street_address', 'city',
  'state', 'zip_code', 'ssn', 'date_of_birth', 'loan_amount', 'loan_purpose',
  'monthly_income', 'annual_income', 'credit_rating', 'employment_status',
  'residence_type', 'residence_months', 'is_military', 'bank_account_type',
  'jornaya_leadid', 'trusted_form_cert_url', 'tcpa_consent'
]

// Type dropdown shared by every place a config author types in a raw value
// the backend needs sent as a real JSON string/number/boolean, not always
// a string - value_map "to" values and rule "default" values.
function TypeSelect({ value, onChange, width = 78 }) {
  return (
    <select
      className="select"
      style={{ width, height: 26, fontSize: 12 }}
      value={value || 'string'}
      onChange={e => onChange(e.target.value)}
    >
      <option value="string">String</option>
      <option value="number">Number</option>
      <option value="boolean">Boolean</option>
    </select>
  )
}

// Value input paired with TypeSelect - a True/False dropdown for boolean
// (so there's no way to type something other than true/false and have it
// silently coerce wrong), a number input for number, else a plain text
// input. Shared by value_map "to" and every rule's Default input so a
// Boolean/Number choice is enforced everywhere, not just in one place.
function TypedValueInput({ type, value, onChange, placeholder, width, height = 26, fontSize = 12 }) {
  if (type === 'boolean') {
    return (
      <select className="select" style={{ width, height, fontSize }} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">-- Select --</option>
        <option value="true">True</option>
        <option value="false">False</option>
      </select>
    )
  }

  return (
    <input
      className="input"
      type={type === 'number' ? 'number' : 'text'}
      style={{ width, height, fontSize }}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="partner-field">
      <label className="partner-field-label">{label}</label>
      {children}
      {hint ? <div className="partner-field-hint">{hint}</div> : null}
    </div>
  )
}

function BuyerOnboardingWizardModal({ buyer, canManageBuyers, onClose, onSaved }) {
  const isEdit = !!buyer
  const buyerId = buyer?.id

  const [step, setStep] = useState(1)
  const [form, setForm] = useState(() => ({
    company_id: buyer?.company_id || '',
    name: buyer?.name || '',
    engine: buyer?.engine || 'waterfall',
    vertical: buyer?.vertical || 'loans',
    accepts_long_form_leads: buyer?.accepts_long_form_leads !== false,
    is_active: buyer?.is_active !== false,
    endpoint_is_active: buyer?.endpoint?.is_active !== false,
    minimum_lead_payout: buyer?.minimum_lead_payout ?? '0.00',
    requires_tier_map: buyer?.requires_tier_map ?? false,
    // Keyed by our tier key (e.g. "25"), value is the buyer's own tier
    // label - and, when requires_tier_map is true, also this buyer's ask
    // price for that tier (see __buyer_ask_price on the backend). Rows are
    // rendered from the vertical's real tier list (fetched below), not a
    // free-form key editor like headers/static_payload, since tier keys
    // are a closed set defined by the vertical, not buyer-chosen.
    tier_map: buyer?.tier_map || {},
    blocked_affiliates: buyer?.blocked_affiliates || [],
    integration_type: buyer?.endpoint?.integration_type || buyer?.integration_type || 'direct',
    // Step 2 & 3: Endpoint setup
    endpoint_url: buyer?.endpoint?.url || buyer?.endpoint_url || '',
    ping_url: buyer?.endpoint?.ping_url || buyer?.ping_url || '',
    endpoint_method: buyer?.endpoint?.method || buyer?.endpoint_method || 'POST',
    request_format: buyer?.endpoint?.request_format || buyer?.request_format || 'json',
    response_format: buyer?.endpoint?.response_format || buyer?.response_format || 'json',
    timeout_seconds: buyer?.endpoint?.timeout_seconds ?? buyer?.timeout_seconds ?? 5,
    max_price_cap: buyer?.endpoint?.max_price_cap ?? buyer?.max_price_cap ?? '',
    allow_counter_offers: buyer?.endpoint?.allow_counter_offers ?? buyer?.allow_counter_offers ?? true,
    min_counter_offer_price: buyer?.endpoint?.min_counter_offer_price ?? buyer?.min_counter_offer_price ?? '0.00',
    counter_offer_mode: buyer?.endpoint?.counter_offer_mode || buyer?.counter_offer_mode || 'resubmit',
    // Server reads allow_counter_offers=True with max_counter_offer_rounds
    // unset/0 as "1 round" - use || (not ??) so a 0 from existing buyers
    // predating this field doesn't render below the input's min="1".
    max_counter_offer_rounds: buyer?.endpoint?.max_counter_offer_rounds || buyer?.max_counter_offer_rounds || 1,
    headers: buyer?.endpoint?.headers
      ? Object.entries(buyer.endpoint.headers).map(([k, v]) => ({ key: k, value: String(v), valueType: inferValueType(v) }))
      : [{ key: '', value: '', valueType: 'string' }],
    static_payload: buyer?.endpoint?.static_payload
      ? Object.entries(buyer.endpoint.static_payload).map(([k, v]) => ({ key: k, value: String(v), valueType: inferValueType(v) }))
      : [{ key: '', value: '', valueType: 'string' }],
    field_mappings: buyer?.endpoint?.field_mapping
      ? Object.entries(buyer.endpoint.field_mapping).map(([k, v]) => parseFieldMappingEntry(k, v))
      : [],
    response_mapping: {
      bid_amount: buyer?.endpoint?.response_mapping?.bid_amount || buyer?.response_mapping?.bid_amount || 'price_offer',
      redirect_url: buyer?.endpoint?.response_mapping?.redirect_url || buyer?.response_mapping?.redirect_url || 'redirect_url',
      counter_offer: buyer?.endpoint?.response_mapping?.counter_offer || buyer?.endpoint?.response_mapping?.counter_price || buyer?.response_mapping?.counter_offer || 'price_offer',
      bid_id: buyer?.endpoint?.response_mapping?.bid_id || buyer?.response_mapping?.bid_id || 'bid_id',
      purchase_url: buyer?.endpoint?.response_mapping?.purchase_url || buyer?.response_mapping?.purchase_url || '',
      decline_field: buyer?.endpoint?.response_mapping?.decline_field || buyer?.response_mapping?.decline_field || '',
      // Stored as the raw comma-separated text the user sees/edits, not an
      // array - splitting/joining on every keystroke would fight the user's
      // cursor. decline_values_explicit tracks whether this buyer actually
      // has the key set (vs. just defaulting to "") so submit can omit it
      // for untouched buyers instead of sending an empty array, which the
      // backend treats as "never declines via decline_field" rather than
      // "use the default vocabulary".
      decline_values: (buyer?.endpoint?.response_mapping?.decline_values || buyer?.response_mapping?.decline_values || []).join(', '),
      decline_values_explicit: Array.isArray(buyer?.endpoint?.response_mapping?.decline_values) || Array.isArray(buyer?.response_mapping?.decline_values)
    },
    // Ping/Post only: post/confirm leg config, sent once a bid_id is captured
    // from the ping leg above - see engine.senders.confirm_buyer().
    post_request_format: buyer?.endpoint?.post_request_format || '',
    post_headers: buyer?.endpoint?.post_headers
      ? Object.entries(buyer.endpoint.post_headers).map(([k, v]) => ({ key: k, value: String(v), valueType: inferValueType(v) }))
      : [],
    post_field_mappings: buyer?.endpoint?.post_field_mapping
      ? Object.entries(buyer.endpoint.post_field_mapping).map(([k, v]) => ({ internal: k, target: String(v) }))
      : [{ internal: 'bid_id', target: 'bid_id' }],
    post_response_mapping: {
      redirect_url: buyer?.endpoint?.post_response_mapping?.redirect_url || '',
      external_lead_id: buyer?.endpoint?.post_response_mapping?.external_lead_id || ''
    },
    // Step 3: Buyer sets
    assigned_buyer_set_ids: buyer?.buyer_set_ids || []
  }))

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [companies, setCompanies] = useState([])
  const [affiliates, setAffiliates] = useState([])
  const [buyerSets, setBuyerSets] = useState([])
  const [verticals, setVerticals] = useState([])
  const [internalFields, setInternalFields] = useState(FALLBACK_INTERNAL_FIELDS)
  const [syntheticFields, setSyntheticFields] = useState([])
  // Fields only ever present on lead_type == "long_form" submissions (see
  // config/api.py::vertical_schema's `long_form_only`) - used to label them
  // in the mapping dropdowns and to exclude them from the field_mappings
  // auto-populate below, since a buyer's mapping shouldn't assume a field
  // that's absent on most (short_form) leads.
  const [longFormOnlyFields, setLongFormOnlyFields] = useState([])
  // {tier, ask}[] for the currently selected vertical, from
  // config/api.py::vertical_schema's `tiers` - drives the Tier Map editor's
  // rows and Step 1 completeness validation for requires_tier_map buyers.
  const [verticalTiers, setVerticalTiers] = useState([])
  const [verticalTiersLoading, setVerticalTiersLoading] = useState(true)
  const autoPopulatedFieldMappings = useRef(false)

  useEffect(() => {
    let ignore = false
    api.get('/companies/', { params: { limit: 50, page_size: 50, per_page: 50 } }).then(res => {
      if (!ignore) {
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.companies || res.data?.data || [])
        setCompanies(list)
      }
    }).catch(() => {})

    api.get('/affiliates/').catch(() => api.get('/partners/')).then(res => {
      if (!ignore && res?.data) setAffiliates(res.data?.items || res.data || [])
    }).catch(() => {})

    api.get('/buyer-sets/').then(res => {
      if (!ignore) setBuyerSets(res.data?.items || res.data?.buyer_sets || res.data || [])
    }).catch(() => {})

    api.get('/config/verticals').then(res => {
      if (!ignore) setVerticals(Array.isArray(res.data) ? res.data : [])
    }).catch(() => {})

    if (buyerId) {
      api.get(`/buyers/${buyerId}`).then(res => {
        if (!ignore && res.data) {
          const d = res.data
          const ep = d.endpoint || {}
          setForm(prev => ({
            ...prev,
            company_id: d.company_id || prev.company_id,
            name: d.name || prev.name,
            engine: d.engine || prev.engine,
            vertical: d.vertical || prev.vertical,
            accepts_long_form_leads: d.accepts_long_form_leads !== false,
            is_active: d.is_active !== false,
            endpoint_is_active: ep.is_active !== false,
            minimum_lead_payout: d.minimum_lead_payout ?? prev.minimum_lead_payout,
            requires_tier_map: d.requires_tier_map ?? prev.requires_tier_map,
            // Not coerced with String()/inferValueType like headers/
            // static_payload below - there's no valueType selector on the
            // Tier Map editor, so a raw string-or-number value round-trips
            // fine through the plain <input> as-is.
            tier_map: d.tier_map || prev.tier_map,
            blocked_affiliates: d.blocked_affiliates || prev.blocked_affiliates,
            integration_type: ep.integration_type || d.integration_type || prev.integration_type,
            endpoint_url: ep.url || d.endpoint_url || prev.endpoint_url,
            ping_url: ep.ping_url || d.ping_url || prev.ping_url,
            endpoint_method: ep.method || d.endpoint_method || prev.endpoint_method,
            request_format: ep.request_format || d.request_format || prev.request_format,
            response_format: ep.response_format || d.response_format || prev.response_format,
            timeout_seconds: ep.timeout_seconds ?? d.timeout_seconds ?? prev.timeout_seconds,
            max_price_cap: ep.max_price_cap ?? d.max_price_cap ?? prev.max_price_cap,
            allow_counter_offers: ep.allow_counter_offers ?? d.allow_counter_offers ?? prev.allow_counter_offers,
            min_counter_offer_price: ep.min_counter_offer_price ?? d.min_counter_offer_price ?? prev.min_counter_offer_price,
            counter_offer_mode: ep.counter_offer_mode || d.counter_offer_mode || prev.counter_offer_mode,
            max_counter_offer_rounds: ep.max_counter_offer_rounds || d.max_counter_offer_rounds || prev.max_counter_offer_rounds,
            headers: ep.headers
              ? Object.entries(ep.headers).map(([k, v]) => ({ key: k, value: String(v), valueType: inferValueType(v) }))
              : prev.headers,
            static_payload: ep.static_payload
              ? Object.entries(ep.static_payload).map(([k, v]) => ({ key: k, value: String(v), valueType: inferValueType(v) }))
              : prev.static_payload,
            field_mappings: ep.field_mapping
              ? Object.entries(ep.field_mapping).map(([k, v]) => parseFieldMappingEntry(k, v))
              : prev.field_mappings,
            response_mapping: {
              bid_amount: ep.response_mapping?.bid_amount || prev.response_mapping.bid_amount,
              redirect_url: ep.response_mapping?.redirect_url || prev.response_mapping.redirect_url,
              counter_offer: ep.response_mapping?.counter_offer || ep.response_mapping?.counter_price || prev.response_mapping.counter_offer,
              bid_id: ep.response_mapping?.bid_id || prev.response_mapping.bid_id,
              purchase_url: ep.response_mapping?.purchase_url || prev.response_mapping.purchase_url,
              decline_field: ep.response_mapping?.decline_field || prev.response_mapping.decline_field,
              decline_values: Array.isArray(ep.response_mapping?.decline_values)
                ? ep.response_mapping.decline_values.join(', ')
                : prev.response_mapping.decline_values,
              decline_values_explicit: Array.isArray(ep.response_mapping?.decline_values) || prev.response_mapping.decline_values_explicit
            },
            post_request_format: ep.post_request_format || prev.post_request_format,
            post_headers: ep.post_headers
              ? Object.entries(ep.post_headers).map(([k, v]) => ({ key: k, value: String(v), valueType: inferValueType(v) }))
              : prev.post_headers,
            post_field_mappings: ep.post_field_mapping
              ? Object.entries(ep.post_field_mapping).map(([k, v]) => ({ internal: k, target: String(v) }))
              : prev.post_field_mappings,
            post_response_mapping: {
              redirect_url: ep.post_response_mapping?.redirect_url || prev.post_response_mapping.redirect_url,
              external_lead_id: ep.post_response_mapping?.external_lead_id || prev.post_response_mapping.external_lead_id
            },
            assigned_buyer_set_ids: d.buyer_set_ids || prev.assigned_buyer_set_ids
          }))
        }
      }).catch(() => {})
    }

    return () => { ignore = true }
  }, [buyerId])

  // Keeps the Advanced Field & Value Mapping dropdown in sync with the
  // backend's canonical field list for whichever vertical is picked in
  // Step 1, instead of a hand-maintained list that silently goes stale
  // every time a vertical adds a field (see config/api.py::vertical_schema).
  useEffect(() => {
    if (!form.vertical) return
    let ignore = false
    // Reset before the fetch resolves, not after - otherwise switching
    // vertical while a previous vertical's fields are still loaded would
    // leave the previous vertical's field list on screen (mislabeled as
    // belonging to the new vertical) until/unless this fetch succeeds.
    setInternalFields(FALLBACK_INTERNAL_FIELDS)
    setLongFormOnlyFields([])
    setVerticalTiers([])
    setVerticalTiersLoading(true)
    api.get(`/config/verticals/${encodeURIComponent(form.vertical)}/schema`)
      .then(res => {
        if (ignore) return
        const fields = Array.isArray(res.data?.fields) ? res.data.fields : []
        if (fields.length) setInternalFields(fields)
        setSyntheticFields(Array.isArray(res.data?.synthetic_fields) ? res.data.synthetic_fields : [])
        setLongFormOnlyFields(Array.isArray(res.data?.long_form_only) ? res.data.long_form_only : [])
        setVerticalTiers(Array.isArray(res.data?.tiers) ? res.data.tiers : [])
      })
      .catch(() => {
        // 404 (unknown vertical) or 403 (not staff) - fall back to the
        // static list rather than leaving the dropdown empty.
      })
      .finally(() => {
        if (!ignore) setVerticalTiersLoading(false)
      })
    return () => { ignore = true }
  }, [form.vertical])

  // Pre-populates the field_mappings editor with one blank row per
  // canonical field the buyer is actually eligible to receive, the first
  // time the real schema loads, but only for a buyer with no saved
  // field_mapping - gives new/never-configured buyers a starting checklist
  // of what to map instead of an empty editor or (as before) one hardcoded
  // example buyer's real values. Buyers that don't accept long_form leads
  // (accepts_long_form_leads=false, see routing.eligibility.filter_eligible
  // on the backend) only ever receive short_form submissions, so their
  // checklist excludes long_form_only fields the same way it always has;
  // buyers that do accept long_form leads get the full field list, since
  // long_form_only fields will actually show up in what they're sent. Runs
  // once per modal open (autoPopulatedFieldMappings ref) so it never
  // clobbers rows the user has since added or removed.
  useEffect(() => {
    if (buyer?.endpoint?.field_mapping) return
    if (autoPopulatedFieldMappings.current) return
    if (internalFields === FALLBACK_INTERNAL_FIELDS) return
    autoPopulatedFieldMappings.current = true
    const defaultFields = form.accepts_long_form_leads
      ? internalFields
      : internalFields.filter(f => !longFormOnlyFields.includes(f))
    setForm(prev => (prev.field_mappings.length > 0 ? prev : {
      ...prev,
      field_mappings: defaultFields.map(f => ({
        internal: f, target: '', defaultValue: '', defaultType: 'string', ruleType: 'simple', castType: 'string',
        valueMap: [], buckets: [], dateFormat: '', concatFields: [], separator: '',
        baseDateField: '', payFrequencyField: '', cycles: ''
      }))
    }))
  }, [internalFields, longFormOnlyFields, buyer, form.accepts_long_form_leads])

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  // Reconciles assigned_buyer_set_ids against the current engine whenever
  // either changes - covers both the user flipping the Engine dropdown AND
  // the buyer-set list arriving asynchronously after mount (so a buyer
  // saved before this feature shipped, holding a since-mismatched
  // assignment, gets cleaned up on open rather than silently failing to
  // save later). Skips while the list is still empty/loading so an engine
  // change made before the fetch resolves doesn't wipe out valid picks it
  // just hasn't seen yet.
  useEffect(() => {
    if (buyerSets.length === 0) return
    setForm(prev => {
      const kept = prev.assigned_buyer_set_ids.filter(id => {
        const bs = buyerSets.find(b => b.id === id)
        return bs ? (bs.engine || 'waterfall') === prev.engine : false
      })
      if (kept.length === prev.assigned_buyer_set_ids.length) return prev
      const removed = prev.assigned_buyer_set_ids.length - kept.length
      toast.error(`${ENGINE_LABELS[prev.engine]} engine: removed ${removed} buyer set(s) that no longer match.`)
      return { ...prev, assigned_buyer_set_ids: kept }
    })
  }, [form.engine, buyerSets])

  const updateResponseMapping = (key, value) => {
    setForm(prev => ({
      ...prev,
      response_mapping: { ...prev.response_mapping, [key]: value }
    }))
  }

  const updatePostResponseMapping = (key, value) => {
    setForm(prev => ({
      ...prev,
      post_response_mapping: { ...prev.post_response_mapping, [key]: value }
    }))
  }

  const updateTierMapValue = (tier, value) => {
    setForm(prev => ({ ...prev, tier_map: { ...prev.tier_map, [tier]: value } }))
  }

  // Key-value row helpers for Headers, Static Payload, and Field Mappings
  const addRow = (fieldKey, newRow) => {
    setForm(prev => ({ ...prev, [fieldKey]: [...prev[fieldKey], newRow] }))
  }

  const updateRow = (fieldKey, index, key, value) => {
    setForm(prev => {
      const next = [...prev[fieldKey]]
      next[index] = { ...next[index], [key]: value }
      return { ...prev, [fieldKey]: next }
    })
  }

  const removeRow = (fieldKey, index) => {
    setForm(prev => ({
      ...prev,
      [fieldKey]: prev[fieldKey].filter((_, i) => i !== index)
    }))
  }

  const addValueMapRow = (mappingIdx) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetMap = [...(next[mappingIdx].valueMap || [])]
      targetMap.push({ from: '', to: '', toType: 'string' })
      next[mappingIdx] = { ...next[mappingIdx], valueMap: targetMap }
      return { ...prev, field_mappings: next }
    })
  }

  const updateValueMapRow = (mappingIdx, mapIdx, key, value) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetMap = [...(next[mappingIdx].valueMap || [])]
      targetMap[mapIdx] = { ...targetMap[mapIdx], [key]: value }
      next[mappingIdx] = { ...next[mappingIdx], valueMap: targetMap }
      return { ...prev, field_mappings: next }
    })
  }

  const removeValueMapRow = (mappingIdx, mapIdx) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetMap = next[mappingIdx].valueMap.filter((_, i) => i !== mapIdx)
      next[mappingIdx] = { ...next[mappingIdx], valueMap: targetMap }
      return { ...prev, field_mappings: next }
    })
  }

  // Numeric range buckets - e.g. residence_months -> TimeAtResidence's
  // "Less than 1 year"/"1 to 2 years"/"More 3 years". Rows are checked in
  // order (first upperBound the value is <= wins); defaultValue on the
  // parent row is used above the last bound. Mirrors the value-map row
  // helpers above.
  const addBucketRow = (mappingIdx) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetBuckets = [...(next[mappingIdx].buckets || [])]
      targetBuckets.push({ upperBound: '', label: '' })
      next[mappingIdx] = { ...next[mappingIdx], buckets: targetBuckets }
      return { ...prev, field_mappings: next }
    })
  }

  const updateBucketRow = (mappingIdx, bucketIdx, key, value) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetBuckets = [...(next[mappingIdx].buckets || [])]
      targetBuckets[bucketIdx] = { ...targetBuckets[bucketIdx], [key]: value }
      next[mappingIdx] = { ...next[mappingIdx], buckets: targetBuckets }
      return { ...prev, field_mappings: next }
    })
  }

  const removeBucketRow = (mappingIdx, bucketIdx) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetBuckets = next[mappingIdx].buckets.filter((_, i) => i !== bucketIdx)
      next[mappingIdx] = { ...next[mappingIdx], buckets: targetBuckets }
      return { ...prev, field_mappings: next }
    })
  }

  // Concat rule's source field list - e.g. Xanadu/PLL's subTwo joining
  // __affiliate_id + __click_subid with "_". Plain strings, not
  // from/to pairs like value_map, since a concat row just lists which
  // fields (in order) get joined - order matters here, so no sorting like
  // the bucket rows get.
  const addConcatFieldRow = (mappingIdx) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetFields = [...(next[mappingIdx].concatFields || [])]
      targetFields.push('')
      next[mappingIdx] = { ...next[mappingIdx], concatFields: targetFields }
      return { ...prev, field_mappings: next }
    })
  }

  const updateConcatFieldRow = (mappingIdx, fieldIdx, value) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetFields = [...(next[mappingIdx].concatFields || [])]
      targetFields[fieldIdx] = value
      next[mappingIdx] = { ...next[mappingIdx], concatFields: targetFields }
      return { ...prev, field_mappings: next }
    })
  }

  const removeConcatFieldRow = (mappingIdx, fieldIdx) => {
    setForm(prev => {
      const next = [...prev.field_mappings]
      const targetFields = next[mappingIdx].concatFields.filter((_, i) => i !== fieldIdx)
      next[mappingIdx] = { ...next[mappingIdx], concatFields: targetFields }
      return { ...prev, field_mappings: next }
    })
  }

  const validateStep = (currentStep) => {
    setError('')
    if (currentStep === 1) {
      if (!form.name.trim()) {
        setError('Buyer Name is required.')
        return false
      }
      if (!isEdit && !form.company_id) {
        setError('Please select a Company.')
        return false
      }
      if (form.minimum_lead_payout !== '' && Number(form.minimum_lead_payout) < 0) {
        setError('Minimum payout must be a valid positive amount.')
        return false
      }
      if (form.requires_tier_map && verticalTiersLoading) {
        setError("Still loading this vertical's tier list - please wait a moment and try again.")
        return false
      }
      // Mirrors companies.models.BuyerProfile.clean() - a requires_tier_map
      // buyer's tier_map must cover every tier this vertical produces, each
      // with a positive numeric value, since a save that fails this gets
      // rejected by the backend anyway (422). Catching it here saves a
      // round trip and points at the specific missing/invalid tier(s).
      // Gated on verticalTiersLoading (not just tiers.length > 0) above so
      // a fast double-click on Next right after checking the box can't
      // slip through while the schema fetch is still in flight.
      if (form.requires_tier_map && verticalTiers.length > 0) {
        const missing = []
        const invalid = []
        verticalTiers.forEach(({ tier }) => {
          const raw = form.tier_map[tier]
          if (raw === undefined || raw === null || String(raw).trim() === '') {
            missing.push(tier)
            return
          }
          const parsed = Number(raw)
          if (!Number.isFinite(parsed) || parsed <= 0) invalid.push(tier)
        })
        if (missing.length) {
          setError(`Tier Map is missing required tier(s): ${missing.join(', ')}.`)
          return false
        }
        if (invalid.length) {
          setError(`Tier Map has non-positive/non-numeric value(s) for tier(s): ${invalid.join(', ')}.`)
          return false
        }
      }
    } else if (currentStep === 2) {
      if (!form.endpoint_url.trim()) {
        setError('Post Webhook URL is required.')
        return false
      }
      const urlStr = form.endpoint_url.trim()
      if (!urlStr.startsWith('http://') && !urlStr.startsWith('https://')) {
        setError('Post Webhook URL must start with http:// or https://')
        return false
      }
      if (form.integration_type === 'ping_post') {
        if (!form.ping_url.trim()) {
          setError('Ping Webhook URL is required for Ping/Post mode.')
          return false
        }
        const pingUrlStr = form.ping_url.trim()
        if (!pingUrlStr.startsWith('http://') && !pingUrlStr.startsWith('https://')) {
          setError('Ping Webhook URL must start with http:// or https://')
          return false
        }
      }
      if (!Number.isInteger(Number(form.timeout_seconds)) || Number(form.timeout_seconds) < 1) {
        setError('Timeout must be a whole number of seconds, at least 1.')
        return false
      }
    }
    return true
  }

  const canGoToStep = (targetStep) => {
    if (targetStep <= step) return true
    for (let s = 1; s < targetStep; s++) {
      if (!validateStep(s)) return false
    }
    return true
  }

  const goToStep = (targetStep) => {
    if (canGoToStep(targetStep)) {
      setError('')
      setStep(targetStep)
    }
  }

  const handleNext = (e) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (validateStep(step)) {
      setStep(prev => prev + 1)
    }
  }

  const handlePrev = (e) => {
    e?.preventDefault()
    e?.stopPropagation()
    setError('')
    setStep(prev => prev - 1)
  }

  async function handleSubmit(e) {
    e?.preventDefault()
    if (!validateStep(step)) return

    setSaving(true)
    setError('')

    const headersObj = {}
    form.headers.forEach(h => {
      if (h.key.trim()) {
        // Explicit Type dropdown, not a "does this look numeric" guess -
        // the old heuristic (!isNaN(val) ? parseFloat/parseInt : val)
        // silently mangled any numeric-looking string meant to stay a
        // string, e.g. a zip code or token with a leading zero ("090210"
        // -> parseInt -> 90210, digit silently dropped). A blank value
        // stays an empty string regardless of type, same as before -
        // coerceTypedValue('', 'boolean') would otherwise silently become
        // false, and Number('') is 0, not "unset".
        headersObj[h.key.trim()] = h.value.trim() ? coerceTypedValue(h.value, h.valueType) : ''
      }
    })

    const staticPayloadObj = {}
    form.static_payload.forEach(s => {
      if (s.key.trim()) {
        staticPayloadObj[s.key.trim()] = s.value.trim() ? coerceTypedValue(s.value, s.valueType) : ''
      }
    })

    const fieldMappingObj = {}
    form.field_mappings.forEach(m => {
      if (!m.internal.trim() || !m.target.trim()) return
      const internal = m.internal.trim()
      const target = m.target.trim()
      const defaultVal = m.defaultValue.trim() ? coerceTypedValue(m.defaultValue, m.defaultType) : undefined

      if (m.ruleType === 'buckets') {
        // Only rows with a real numeric upper bound survive - Number('')
        // and Number('-') both look "not empty" but coerce to NaN, which
        // JSON.stringify silently turns into `null` on the wire (a bucket
        // boundary of null reaching the backend with no error anywhere).
        // Sorted ascending since the backend's rule is "first upper bound
        // the value is <= wins" - an admin entering rows out of order
        // would otherwise get silently wrong routing on every lead.
        const buckets = (m.buckets || [])
          .filter(b => b.upperBound.trim() !== '' && !Number.isNaN(Number(b.upperBound.trim())) && b.label.trim())
          .map(b => [Number(b.upperBound.trim()), b.label.trim()])
          .sort((a, b) => a[0] - b[0])
        if (buckets.length === 0) return
        fieldMappingObj[internal] = { target, default: defaultVal, buckets }
      } else if (m.ruleType === 'date_format') {
        // No output format means no way to reformat the date - skip the
        // rule entirely rather than silently sending date_format: "" to
        // the backend (which would just pass the raw ISO date through,
        // quietly not doing what "Date Format" rule type implies it does).
        if (!m.dateFormat.trim()) return
        fieldMappingObj[internal] = { target, default: defaultVal, date_format: m.dateFormat.trim() }
      } else if (m.ruleType === 'now') {
        // Doesn't read `internal` from the lead at all - stamps the
        // current UTC time at send time (engine/senders.py map_payload's
        // `now` rule). Same "no output format, no rule" guard as
        // date_format above.
        if (!m.dateFormat.trim()) return
        fieldMappingObj[internal] = { target, now: true, date_format: m.dateFormat.trim() }
      } else if (m.ruleType === 'value_map') {
        const value_map = {};
        (m.valueMap || []).forEach(vm => {
          // A blank "To" (e.g. a Boolean row still sitting at "-- Select
          // --") must not coerce to a real value - coerceTypedValue('',
          // 'boolean') would silently become `false`, and Number('') is 0,
          // not NaN, so an untouched Number row would silently become 0.
          // Skip the row entirely rather than write a value nobody chose.
          if (vm.from.trim() && vm.to.trim()) {
            value_map[vm.from.trim()] = coerceTypedValue(vm.to, vm.toType)
          }
        })
        fieldMappingObj[internal] = { target, default: defaultVal, value_map }
      } else if (m.ruleType === 'concat') {
        // No source fields means nothing to join - skip the rule entirely
        // rather than sending an empty "fields" list the backend would
        // just always omit the key for (engine/senders.py's concat rule).
        const fields = (m.concatFields || []).map(f => f.trim()).filter(Boolean)
        if (fields.length === 0) return
        fieldMappingObj[internal] = { target, default: defaultVal, rule: 'concat', fields, separator: m.separator }
      } else if (m.ruleType === 'pay_cycle_offset') {
        // No base date field or no output format means nothing to compute
        // or nowhere to put it - skip the rule entirely rather than
        // sending a shape the backend's map_payload would reject outright
        // (engine/senders.py raises ValueError on an unrecognized `rule`,
        // and a missing base_date_field/date_format leaves the offset
        // uncomputable). pay_frequency_field defaults to "pay_frequency"
        // to match the backend's own default, same as leaving it blank
        // meaning "use the standard field".
        if (!m.baseDateField.trim() || !m.dateFormat.trim()) return
        // cycles is optional - blank/invalid is left out entirely rather
        // than sent as a bogus value, since the backend already falls back
        // to 1 (the next pay date) for a missing or invalid cycles key
        // (engine/senders.py map_payload's pay_cycle_offset rule).
        const parsedCycles = parseInt(m.cycles, 10)
        const cyclesValid = Number.isInteger(parsedCycles) && parsedCycles >= 1
        fieldMappingObj[internal] = {
          target,
          rule: 'pay_cycle_offset',
          base_date_field: m.baseDateField.trim(),
          pay_frequency_field: (m.payFrequencyField || '').trim() || 'pay_frequency',
          date_format: m.dateFormat.trim(),
          ...(cyclesValid ? { cycles: parsedCycles } : {})
        }
      } else if (m.castType && m.castType !== 'string') {
        // Backend rejects "cast" combined with value_map/buckets/date_format/
        // rule/now outright (engine.senders.map_payload), so this only ever
        // reaches here for a true Simple Rename row - safe to emit the
        // advanced dict form instead of a bare string.
        fieldMappingObj[internal] = { target, cast: m.castType }
      } else {
        fieldMappingObj[internal] = target
      }
    })

    const postHeadersObj = {}
    form.post_headers.forEach(h => {
      if (h.key.trim()) postHeadersObj[h.key.trim()] = h.value.trim() ? coerceTypedValue(h.value, h.valueType) : ''
    })

    const postFieldMappingObj = {}
    form.post_field_mappings.forEach(m => {
      if (m.internal.trim() && m.target.trim()) postFieldMappingObj[m.internal.trim()] = m.target.trim()
    })

    const verticalTierKeys = new Set(verticalTiers.map(t => t.tier))

    const payload = {
      company_id: form.company_id ? Number(form.company_id) : undefined,
      name: form.name.trim(),
      engine: form.engine,
      vertical: form.vertical,
      accepts_long_form_leads: form.accepts_long_form_leads,
      is_active: form.is_active,
      minimum_lead_payout: form.minimum_lead_payout === '' ? '0.00' : String(form.minimum_lead_payout),
      requires_tier_map: form.requires_tier_map,
      // Scoped to the current vertical's real tier keys, not just non-empty
      // values - switching Vertical mid-session doesn't clear form.tier_map
      // (the table just re-renders against the new verticalTiers), so a
      // stale entry from a previously-selected vertical could otherwise
      // ride along, and - if the two verticals happen to share a tier key
      // string like "25" - silently submit as if it were deliberately
      // entered data for the new vertical.
      tier_map: Object.fromEntries(
        Object.entries(form.tier_map).filter(
          ([k, v]) => verticalTierKeys.has(k) && String(v ?? '').trim() !== ''
        )
      ),
      blocked_affiliates: form.blocked_affiliates,
      endpoint: {
        url: form.endpoint_url.trim(),
        ping_url: form.integration_type === 'ping_post' ? (form.ping_url.trim() || '') : (form.ping_url.trim() || ''),
        integration_type: form.integration_type,
        method: form.endpoint_method,
        request_format: form.request_format,
        response_format: form.response_format,
        timeout_seconds: Number(form.timeout_seconds),
        max_price_cap: form.max_price_cap !== '' ? String(form.max_price_cap) : undefined,
        allow_counter_offers: form.allow_counter_offers,
        min_counter_offer_price: form.allow_counter_offers && form.min_counter_offer_price !== '' ? String(form.min_counter_offer_price) : '0.00',
        counter_offer_mode: form.allow_counter_offers ? form.counter_offer_mode : 'resubmit',
        max_counter_offer_rounds: form.allow_counter_offers
          ? (form.counter_offer_mode === 'purchase_url' ? 1 : Math.min(5, Math.max(1, Number(form.max_counter_offer_rounds) || 1)))
          : 0,
        is_active: form.endpoint_is_active,
        headers: headersObj,
        static_payload: staticPayloadObj,
        response_mapping: {
          bid_amount: form.response_mapping.bid_amount.trim(),
          redirect_url: form.response_mapping.redirect_url.trim(),
          counter_offer: form.response_mapping.counter_offer.trim(),
          ...(form.integration_type === 'ping_post' && form.response_mapping.bid_id.trim()
            ? { bid_id: form.response_mapping.bid_id.trim() }
            : {}),
          ...(form.allow_counter_offers && form.counter_offer_mode === 'purchase_url' && form.response_mapping.purchase_url.trim()
            ? { purchase_url: form.response_mapping.purchase_url.trim() }
            : {}),
          ...(form.response_mapping.decline_field.trim()
            ? { decline_field: form.response_mapping.decline_field.trim() }
            : {}),
          // Only emit decline_values when the buyer actually has it configured
          // (fetched with the key set, or the user edited this field) - an
          // untouched buyer defaults to '', and sending decline_values: []
          // for it would tell the backend "never decline via decline_field"
          // instead of leaving the default vocabulary in place.
          ...(form.response_mapping.decline_values_explicit
            ? { decline_values: form.response_mapping.decline_values.split(',').map(v => v.trim()).filter(Boolean) }
            : {})
        },
        field_mapping: fieldMappingObj,
        ...(form.integration_type === 'ping_post'
          ? {
              post_request_format: form.post_request_format,
              post_headers: postHeadersObj,
              post_field_mapping: postFieldMappingObj,
              post_response_mapping: {
                redirect_url: form.post_response_mapping.redirect_url.trim(),
                external_lead_id: form.post_response_mapping.external_lead_id.trim()
              }
            }
          : {})
      },
      buyer_set_ids: form.assigned_buyer_set_ids
    }

    try {
      if (isEdit) {
        await api.patch(`/buyers/${buyerId}`, payload)
        toast.success('Buyer profile updated')
      } else {
        if (!form.company_id) {
          setError('Please select a company.')
          setSaving(false)
          return
        }
        payload.company_id = Number(form.company_id)
        await api.post('/buyers/', payload)
        toast.success('Buyer onboarded successfully!')
      }

      onSaved()
      onClose()
    } catch (err) {
      // 422s from this endpoint carry a business-reason detail string
      // (engine mismatch/exclusivity conflicts) written for the person
      // configuring buyers to actually understand - surface it verbatim
      // rather than a generic failure toast.
      const detail = err.response?.data?.detail || err.message || 'Error saving buyer.'
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail))
    } finally {
      setSaving(false)
    }
  }

  const eligibleBuyerSets = buyerSets.filter(bs => (bs.engine || 'waterfall') === form.engine)

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal partner-modal partner-modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="partner-modal-header" style={{ paddingBottom: 12 }}>
          <div>
            <h2>{isEdit ? `Edit ${form.name || 'Buyer'}` : 'Onboard New Buyer'}</h2>
            <p>{isEdit ? 'Update buyer configuration and endpoints.' : 'Configure general details, webhook endpoints, and routing sets.'}</p>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Multi-step progress bar */}
        <div className="wizard-steps">
          <div
            className={`wizard-step-item ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}
            onClick={() => goToStep(1)}
            style={{ cursor: 'pointer' }}
          >
            <span className="step-number">{step > 1 ? <Check size={12} /> : '1'}</span>
            <span>General & Payout</span>
          </div>
          <div
            className={`wizard-step-item ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}
            onClick={() => goToStep(2)}
            style={{ cursor: 'pointer' }}
          >
            <span className="step-number">{step > 2 ? <Check size={12} /> : '2'}</span>
            <span>Endpoint & Mapping</span>
          </div>
          <div
            className={`wizard-step-item ${step === 3 ? 'active' : ''}`}
            onClick={() => goToStep(3)}
            style={{ cursor: 'pointer' }}
          >
            <span className="step-number">3</span>
            <span>Buyer Sets</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="partner-modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: 24 }}>
          {step === 1 && (
            <div className="fade-in">
              <section className="partner-modal-section">
                <div className="partner-modal-section-head">
                  <h3>Step 1: General Info & Payout Rules</h3>
                </div>

                <div className="partner-modal-grid partner-modal-grid-2">
                  <Field label="Buyer Name *">
                    <input
                      className="input"
                      value={form.name}
                      onChange={e => updateField('name', e.target.value)}
                      placeholder="e.g. LeadGen Pros"
                    />
                  </Field>

                  {!isEdit ? (
                    <Field label="Company *">
                      <select
                        className="select"
                        value={form.company_id}
                        onChange={e => updateField('company_id', e.target.value)}
                      >
                        <option value="">-- Select Company --</option>
                        {companies.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </Field>
                  ) : (
                    <Field label="Company">
                      <input className="input" value={companies.find(c => c.id === form.company_id)?.name || `Company ID: ${form.company_id}`} disabled />
                    </Field>
                  )}

                  <Field label="Routing Engine Mode (integration_type)">
                    <select
                      className="select"
                      value={form.integration_type}
                      onChange={e => updateField('integration_type', e.target.value)}
                    >
                      <option value="direct">Direct Buy / Single POST (default)</option>
                      <option value="ping_post">Ping / Post (Two-Phase Auction)</option>
                    </select>
                  </Field>

                  <Field label="Engine *" hint="Must match every Buyer Set this buyer is assigned to on Step 3">
                    <select
                      className="select"
                      value={form.engine}
                      onChange={e => updateField('engine', e.target.value)}
                    >
                      {ENGINE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Vertical" hint="Determines the canonical field list offered in Step 2's Advanced Field & Value Mapping">
                    <select
                      className="select"
                      value={form.vertical}
                      onChange={e => updateField('vertical', e.target.value)}
                    >
                      {verticals.length === 0 ? (
                        <option value={form.vertical}>{form.vertical}</option>
                      ) : (
                        verticals.map(v => (
                          <option key={v.identifier} value={v.identifier}>{v.label}</option>
                        ))
                      )}
                    </select>
                  </Field>

                  <Field label="Accepted Lead Types" hint="Long-form leads carry extra fields (see the (long_form only) tags below); unchecking this excludes this buyer from long-form routing entirely">
                    <label className="partner-check-label" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={form.accepts_long_form_leads}
                        onChange={e => updateField('accepts_long_form_leads', e.target.checked)}
                      />
                      Accepts Long-Form Leads
                    </label>
                  </Field>

                  <Field label="Minimum Lead Payout ($)" hint="Floor price below which bids are rejected">
                    <input
                      className="input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.minimum_lead_payout}
                      onChange={e => updateField('minimum_lead_payout', e.target.value)}
                      placeholder="0.00"
                    />
                  </Field>

                  <Field label="Buyer Status" hint="Also requires the endpoint on Step 2 to be active, or leads won't route to this buyer">
                    <label className="partner-check-label" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={e => updateField('is_active', e.target.checked)}
                      />
                      Is Active
                    </label>
                  </Field>
                </div>
              </section>

              <section className="partner-modal-section" style={{ marginTop: 20 }}>
                <div className="partner-modal-section-head">
                  <h3>Tier Map</h3>
                  <p>
                    Translates our lead tiers to this buyer's own tier labels. Also doubles as
                    this buyer's ask price per tier when Require Full Tier Map is on, available
                    below in Advanced Field &amp; Value Mapping as a mappable field.
                  </p>
                </div>

                <label className="partner-check-label" style={{ marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={form.requires_tier_map}
                    onChange={e => updateField('requires_tier_map', e.target.checked)}
                  />
                  Require Full Tier Map
                </label>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  {form.requires_tier_map
                    ? 'Every tier below must have a positive numeric value - that value is sent to this buyer as their ask price, and a lead in an unmapped tier will never be dispatched to them.'
                    : 'Optional/best-effort - an unmapped tier is sent through unchanged with our own ask price, instead of being excluded.'}
                </div>

                {verticalTiers.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {form.vertical ? 'Loading tiers for this vertical…' : 'Select a vertical to configure its tier map.'}
                  </div>
                ) : (
                  <table className="mapping-table">
                    <thead>
                      <tr>
                        <th>Our Tier</th>
                        <th>Our Ask ($)</th>
                        <th>{form.requires_tier_map ? "Buyer's Tier Label / Ask Price ($) *" : "Buyer's Tier Label"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {verticalTiers.map(({ tier, ask }) => (
                        <tr key={tier}>
                          <td>{tier}</td>
                          <td>{ask != null ? `$${Number(ask).toFixed(2)}` : '—'}</td>
                          <td>
                            <input
                              className="input"
                              style={{ height: 32, fontSize: 13 }}
                              placeholder={form.requires_tier_map ? 'e.g. 150' : 'defaults to our own tier unchanged'}
                              value={form.tier_map[tier] ?? ''}
                              onChange={e => updateTierMapValue(tier, e.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="partner-modal-section" style={{ marginTop: 20 }}>
                <div className="partner-modal-section-head">
                  <h3>Blocked Affiliates</h3>
                  <p>Leads from selected affiliates will not be routed to this buyer.</p>
                </div>
                <div style={{ maxHeight: 120, overflowY: 'auto', background: 'var(--bg-raised)', padding: 12, borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                  {affiliates.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No affiliates available</div>
                  ) : (
                    affiliates.map(aff => {
                      const affId = aff.id || aff.identifier || aff.code || aff.name
                      const isChecked = form.blocked_affiliates.includes(affId)
                      return (
                        <label key={affId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={e => {
                              if (e.target.checked) {
                                updateField('blocked_affiliates', [...form.blocked_affiliates, affId])
                              } else {
                                updateField('blocked_affiliates', form.blocked_affiliates.filter(id => id !== affId))
                              }
                            }}
                          />
                          <span>{aff.name || aff.identifier || affId}</span>
                        </label>
                      )
                    })
                  )}
                </div>
              </section>
            </div>
          )}

          {step === 2 && (
            <div className="fade-in">
              <section className="partner-modal-section">
                <div className="partner-modal-section-head">
                  <h3>Step 2: Advanced Endpoint & Webhook Setup</h3>
                  <p>Configure HTTP method, body payload format, headers, static defaults, and field mappings.</p>
                </div>

                <div className="partner-modal-grid partner-modal-grid-2">
                  <Field label="Post Webhook URL *" hint="e.g. https://api.buyer.com/post-lead">
                    <input
                      className="input"
                      type="url"
                      value={form.endpoint_url}
                      onChange={e => updateField('endpoint_url', e.target.value)}
                      placeholder="https://api.buyer.com/post-lead"
                    />
                  </Field>

                  {form.integration_type === 'ping_post' && (
                    <Field label="Ping Webhook URL *" hint="URL for bid request (https://api.buyer.com/ping)">
                      <input
                        className="input"
                        type="url"
                        value={form.ping_url}
                        onChange={e => updateField('ping_url', e.target.value)}
                        placeholder="https://api.buyer.com/ping"
                      />
                    </Field>
                  )}

                  <Field label="Endpoint Status" hint="Must be active for this buyer to receive leads, separately from Buyer Status above">
                    <label className="partner-check-label" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={form.endpoint_is_active}
                        onChange={e => updateField('endpoint_is_active', e.target.checked)}
                      />
                      Endpoint Is Active
                    </label>
                  </Field>
                  <Field label="HTTP Method">
                    <select
                      className="select"
                      value={form.endpoint_method}
                      onChange={e => updateField('endpoint_method', e.target.value)}
                    >
                      <option value="POST">POST (default)</option>
                      <option value="GET">GET</option>
                    </select>
                  </Field>

                  <Field
                    label="Timeout (seconds)"
                    hint={form.allow_counter_offers
                      ? "How long to wait for this buyer's response before treating the request as failed. Note: with Counter-Offers enabled, the server caps the whole negotiation (all rounds combined) at 6s regardless of this value — raising this alone won't give a counter-offer buyer more time."
                      : "How long to wait for this buyer's response before treating the request as failed. Loan-vertical buyers doing fraud/underwriting checks often need 30-60s."}
                  >
                    <input
                      className="input"
                      type="number"
                      step="1"
                      min="1"
                      max="120"
                      value={form.timeout_seconds}
                      onChange={e => updateField('timeout_seconds', e.target.value)}
                    />
                  </Field>

                  <Field label="Payload Body Format (request_format)">
                    <select
                      className="select"
                      value={form.request_format}
                      onChange={e => updateField('request_format', e.target.value)}
                    >
                      <option value="json">JSON (application/json)</option>
                      <option value="form">Form URL Encoded (application/x-www-form-urlencoded)</option>
                    </select>
                  </Field>

                  <Field label="Response Format (response_format)">
                    <select
                      className="select"
                      value={form.response_format}
                      onChange={e => updateField('response_format', e.target.value)}
                    >
                      <option value="json">JSON (default)</option>
                      <option value="xml">XML (e.g. flat &lt;Response&gt;...&lt;/Response&gt; documents)</option>
                    </select>
                  </Field>

                  <Field label="Max Asking Price Cap ($) (max_price_cap)" hint="Caps initial request ask price while accepting higher winning bids">
                    <input
                      className="input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.max_price_cap}
                      onChange={e => updateField('max_price_cap', e.target.value)}
                      placeholder="2.00"
                    />
                  </Field>
                </div>
              </section>

              {/* Price Retries / Counter-Offers */}
              <section className="partner-modal-section" style={{ marginTop: 20 }}>
                <div className="partner-modal-section-head">
                  <h3>Price Retries & Counter-Offers</h3>
                  <p>Allow buyer to submit a lower counter-offer if primary bid is below floor price (server caps negotiation at 5 rounds).</p>
                </div>

                <div className="partner-modal-grid partner-modal-grid-2">
                  <Field label="Allow Counter-Offers">
                    <label className="partner-check-label" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={form.allow_counter_offers}
                        onChange={e => updateField('allow_counter_offers', e.target.checked)}
                      />
                      Enable Counter-Offer Bidding
                    </label>
                  </Field>

                  {form.allow_counter_offers && (
                    <Field label="Minimum Counter-Offer Price ($)" hint="e.g. 0.00">
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.min_counter_offer_price}
                        onChange={e => updateField('min_counter_offer_price', e.target.value)}
                        placeholder="0.00"
                      />
                    </Field>
                  )}

                  {form.allow_counter_offers && (
                    <Field label="Counter-Offer Mode" hint="Resubmit rebuilds and resends the full lead; Purchase URL pings a confirmation URL the buyer returns instead">
                      <select
                        className="select"
                        value={form.counter_offer_mode}
                        onChange={e => updateField('counter_offer_mode', e.target.value)}
                      >
                        <option value="resubmit">Resubmit Lead</option>
                        <option value="purchase_url">Purchase URL</option>
                      </select>
                    </Field>
                  )}

                  {form.allow_counter_offers && (
                    <Field
                      label="Max Negotiation Rounds"
                      hint={form.counter_offer_mode === 'purchase_url'
                        ? 'Purchase URL mode is capped at 1 round'
                        : 'How many counter-offer rounds to allow (server caps at 5)'}
                    >
                      <input
                        className="input"
                        type="number"
                        step="1"
                        min="1"
                        max="5"
                        disabled={form.counter_offer_mode === 'purchase_url'}
                        value={form.counter_offer_mode === 'purchase_url' ? 1 : form.max_counter_offer_rounds}
                        onChange={e => updateField('max_counter_offer_rounds', e.target.value)}
                      />
                    </Field>
                  )}
                </div>
              </section>

              {/* Custom HTTP Headers */}
              <section className="partner-modal-section" style={{ marginTop: 20 }}>
                <div className="partner-modal-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3>Custom HTTP Headers</h3>
                    <p>Static headers sent with every request (e.g. Authorization, X-API-Key).</p>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => addRow('headers', { key: '', value: '', valueType: 'string' })}>
                    + Add Custom Header
                  </button>
                </div>

                <table className="mapping-table">
                  <thead>
                    <tr>
                      <th>Header Key</th>
                      <th>Header Value</th>
                      <th>Type</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.headers.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            className="input"
                            style={{ height: 32, fontSize: 13 }}
                            placeholder="e.g. Authorization"
                            value={row.key}
                            onChange={e => updateRow('headers', idx, 'key', e.target.value)}
                          />
                        </td>
                        <td>
                          <TypedValueInput
                            type={row.valueType}
                            value={row.value}
                            onChange={val => updateRow('headers', idx, 'value', val)}
                            placeholder="e.g. Bearer secret_token_123"
                            height={32}
                            fontSize={13}
                          />
                        </td>
                        <td>
                          <TypeSelect
                            value={row.valueType}
                            onChange={val => {
                              updateRow('headers', idx, 'valueType', val)
                              updateRow('headers', idx, 'value', '')
                            }}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon"
                            style={{ padding: 4, height: 28, width: 28 }}
                            onClick={() => removeRow('headers', idx)}
                          >
                            <Trash2 size={13} style={{ color: 'var(--red)' }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {/* Static Payload Defaults */}
              <section className="partner-modal-section" style={{ marginTop: 20 }}>
                <div className="partner-modal-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3>Static Payload Parameters</h3>
                    <p>Fixed parameters injected into every payload sent to this buyer (e.g. sub_id, key, consentSms).</p>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => addRow('static_payload', { key: '', value: '', valueType: 'string' })}>
                    + Add Static Field
                  </button>
                </div>

                <table className="mapping-table">
                  <thead>
                    <tr>
                      <th>Parameter Key</th>
                      <th>Value</th>
                      <th>Type</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.static_payload.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <input
                            className="input"
                            style={{ height: 32, fontSize: 13 }}
                            placeholder="e.g. key, sub2, consentSms"
                            value={row.key}
                            onChange={e => updateRow('static_payload', idx, 'key', e.target.value)}
                          />
                        </td>
                        <td>
                          <TypedValueInput
                            type={row.valueType}
                            value={row.value}
                            onChange={val => updateRow('static_payload', idx, 'value', val)}
                            placeholder="e.g. abf7729b-90e0-4828, 1"
                            height={32}
                            fontSize={13}
                          />
                        </td>
                        <td>
                          <TypeSelect
                            value={row.valueType}
                            onChange={val => {
                              updateRow('static_payload', idx, 'valueType', val)
                              updateRow('static_payload', idx, 'value', '')
                            }}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon"
                            style={{ padding: 4, height: 28, width: 28 }}
                            onClick={() => removeRow('static_payload', idx)}
                          >
                            <Trash2 size={13} style={{ color: 'var(--red)' }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {/* Response Field Mapping */}
              <section className="partner-modal-section" style={{ marginTop: 20 }}>
                <div className="partner-modal-section-head">
                  <h3>Response Field Mapping</h3>
                  <p>Dot-notation JSON path mapping for parsing buyer responses.</p>
                </div>

                <div className="partner-modal-grid partner-modal-grid-2">
                  <Field label="Bid Price Path" hint="e.g. price_offer or data.bid">
                    <input
                      className="input"
                      value={form.response_mapping.bid_amount}
                      onChange={e => updateResponseMapping('bid_amount', e.target.value)}
                      placeholder="price_offer"
                    />
                  </Field>

                  <Field label="Redirect URL Path" hint="e.g. redirect_url or data.landing_page">
                    <input
                      className="input"
                      value={form.response_mapping.redirect_url}
                      onChange={e => updateResponseMapping('redirect_url', e.target.value)}
                      placeholder="redirect_url"
                    />
                  </Field>

                  <Field label="Counter-Offer Path" hint="e.g. price_offer or counter_price">
                    <input
                      className="input"
                      value={form.response_mapping.counter_offer}
                      onChange={e => updateResponseMapping('counter_offer', e.target.value)}
                      placeholder="price_offer"
                    />
                  </Field>

                  {form.integration_type === 'ping_post' && (
                    <Field label="Bid ID Path" hint="Ping response field carrying the bid_id to confirm later, e.g. bid_id">
                      <input
                        className="input"
                        value={form.response_mapping.bid_id}
                        onChange={e => updateResponseMapping('bid_id', e.target.value)}
                        placeholder="bid_id"
                      />
                    </Field>
                  )}

                  {form.allow_counter_offers && form.counter_offer_mode === 'purchase_url' && (
                    <Field label="Purchase URL Path" hint="Response field carrying the buyer's purchase/confirmation URL to ping on counter-offer accept, e.g. purchase_url">
                      <input
                        className="input"
                        value={form.response_mapping.purchase_url}
                        onChange={e => updateResponseMapping('purchase_url', e.target.value)}
                        placeholder="purchase_url"
                      />
                    </Field>
                  )}

                  <Field label="Decline Field Path" hint="Response field carrying the buyer's decline/status code, e.g. ResponseCode. Leave blank to use the default status/message check.">
                    <input
                      className="input"
                      value={form.response_mapping.decline_field}
                      onChange={e => updateResponseMapping('decline_field', e.target.value)}
                      placeholder="ResponseCode"
                    />
                  </Field>

                  <Field label="Decline Values" hint="Comma-separated, matched case-insensitively against the Decline Field value, e.g. MissingRequiredField, NoBuyer, InvalidValue">
                    <input
                      className="input"
                      value={form.response_mapping.decline_values}
                      onChange={e => setForm(prev => ({
                        ...prev,
                        response_mapping: {
                          ...prev.response_mapping,
                          decline_values: e.target.value,
                          decline_values_explicit: true
                        }
                      }))}
                      placeholder="MissingRequiredField, NoBuyer, InvalidValue"
                    />
                  </Field>
                </div>
              </section>

              {/* Post/Confirm Leg (Ping/Post buyers only) */}
              {form.integration_type === 'ping_post' && (
                <section className="partner-modal-section" style={{ marginTop: 20 }}>
                  <div className="partner-modal-section-head">
                    <h3>Post/Confirm Leg</h3>
                    <p>
                      Once the ping leg returns a winning bid_id, this second request confirms the sale at
                      the Post Webhook URL above. Falls back to the ping leg's headers/body format when left blank.
                    </p>
                  </div>

                  <div className="partner-modal-grid partner-modal-grid-2">
                    <Field label="Post/Confirm Body Format" hint="Defaults to the ping leg's Payload Body Format if unset">
                      <select
                        className="select"
                        value={form.post_request_format}
                        onChange={e => updateField('post_request_format', e.target.value)}
                      >
                        <option value="">Same as Payload Body Format above</option>
                        <option value="json">JSON (application/json)</option>
                        <option value="form">Form URL Encoded (application/x-www-form-urlencoded)</option>
                      </select>
                    </Field>

                    <Field label="Confirmed Redirect URL Path" hint="e.g. TYP_link">
                      <input
                        className="input"
                        value={form.post_response_mapping.redirect_url}
                        onChange={e => updatePostResponseMapping('redirect_url', e.target.value)}
                        placeholder="TYP_link"
                      />
                    </Field>

                    <Field label="Buyer's Lead ID Path" hint="e.g. LeadID">
                      <input
                        className="input"
                        value={form.post_response_mapping.external_lead_id}
                        onChange={e => updatePostResponseMapping('external_lead_id', e.target.value)}
                        placeholder="LeadID"
                      />
                    </Field>
                  </div>

                  <div className="partner-modal-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                    <div>
                      <h4 style={{ margin: 0 }}>Post/Confirm Headers</h4>
                      <p style={{ margin: '2px 0 0' }}>Falls back to Custom HTTP Headers above if left empty.</p>
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => addRow('post_headers', { key: '', value: '', valueType: 'string' })}>
                      + Add Header
                    </button>
                  </div>
                  <table className="mapping-table">
                    <thead>
                      <tr>
                        <th>Header Key</th>
                        <th>Header Value</th>
                        <th>Type</th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.post_headers.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            <input
                              className="input"
                              style={{ height: 32, fontSize: 13 }}
                              placeholder="e.g. Authorization"
                              value={row.key}
                              onChange={e => updateRow('post_headers', idx, 'key', e.target.value)}
                            />
                          </td>
                          <td>
                            <TypedValueInput
                              type={row.valueType}
                              value={row.value}
                              onChange={val => updateRow('post_headers', idx, 'value', val)}
                              placeholder="e.g. Bearer secret_token_123"
                              height={32}
                              fontSize={13}
                            />
                          </td>
                          <td>
                            <TypeSelect
                              value={row.valueType}
                              onChange={val => {
                                updateRow('post_headers', idx, 'valueType', val)
                                updateRow('post_headers', idx, 'value', '')
                              }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon"
                              style={{ padding: 4, height: 28, width: 28 }}
                              onClick={() => removeRow('post_headers', idx)}
                            >
                              <Trash2 size={13} style={{ color: 'var(--red)' }} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="partner-modal-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                    <div>
                      <h4 style={{ margin: 0 }}>Post/Confirm Field Mapping</h4>
                      <p style={{ margin: '2px 0 0' }}>
                        What gets sent to confirm the sale - the captured bid_id plus any other field this buyer
                        needs on the confirm call (e.g. ssn). Defaults to {'{'}"bid_id": "bid_id"{'}'}.
                      </p>
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => addRow('post_field_mappings', { internal: '', target: '' })}>
                      + Add Field
                    </button>
                  </div>
                  <table className="mapping-table">
                    <thead>
                      <tr>
                        <th>Internal Field</th>
                        <th>Buyer Target Field</th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.post_field_mappings.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            <input
                              className="input"
                              style={{ height: 32, fontSize: 13 }}
                              placeholder="e.g. bid_id, ssn"
                              value={row.internal}
                              onChange={e => updateRow('post_field_mappings', idx, 'internal', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className="input"
                              style={{ height: 32, fontSize: 13 }}
                              placeholder="e.g. bid_id"
                              value={row.target}
                              onChange={e => updateRow('post_field_mappings', idx, 'target', e.target.value)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon"
                              style={{ padding: 4, height: 28, width: 28 }}
                              onClick={() => removeRow('post_field_mappings', idx)}
                            >
                              <Trash2 size={13} style={{ color: 'var(--red)' }} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {/* Advanced Field & Value Mapping */}
              <section className="partner-modal-section" style={{ marginTop: 20 }}>
                <div className="partner-modal-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3>Advanced Field & Value Mapping</h3>
                    <p>Simple key renaming, enum value maps, numeric range buckets (e.g. months → a bucketed string), or date reformatting (our dates are stored as YYYY-MM-DD).</p>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => addRow('field_mappings', { internal: '', target: '', defaultValue: '', defaultType: 'string', ruleType: 'simple', castType: 'string', valueMap: [], buckets: [], dateFormat: '', concatFields: [], separator: '', baseDateField: '', payFrequencyField: '', cycles: '' })}>
                    + Add Field Mapping
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                  {form.field_mappings.map((m, idx) => (
                    <div key={idx} style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {m.ruleType === 'now' || m.ruleType === 'concat' || m.ruleType === 'pay_cycle_offset' ? (
                          // Neither "now", "concat", nor "pay_cycle_offset" reads
                          // `internal` as a real lead field - "now" stamps the
                          // current time, "concat" joins whatever's listed below,
                          // "pay_cycle_offset" reads Base Date Field/Pay Frequency
                          // Field below instead - so there's nothing to pick from
                          // the fixed field list below. Free-text a synthetic key
                          // instead (convention: prefix with `__`, e.g.
                          // `__submitted_at`/`__affiliate_subid`/`__next_pay_date2`)
                          // so it can't collide with an actual lead field name.
                          <input
                            className="input"
                            style={{ flex: 1, minWidth: 140, height: 32, fontSize: 13 }}
                            placeholder={m.ruleType === 'concat' ? 'e.g. __affiliate_subid' : 'e.g. __submitted_at'}
                            value={m.internal}
                            onChange={e => updateRow('field_mappings', idx, 'internal', e.target.value)}
                          />
                        ) : (
                        <select
                          className="select"
                          style={{ flex: 1, minWidth: 140, height: 32, fontSize: 13 }}
                          value={m.internal}
                          onChange={e => updateRow('field_mappings', idx, 'internal', e.target.value)}
                        >
                          <option value="">-- Select Internal Field --</option>
                          {/* m.internal may hold a field saved under an older/different
                              vertical's schema than the one currently selected - keep it
                              selectable (as an extra option) rather than silently
                              reverting the row to blank. */}
                          {m.internal && !internalFields.includes(m.internal) && (
                            <option value={m.internal}>{m.internal} (not in {form.vertical} schema)</option>
                          )}
                          {internalFields.map(f => (
                            <option key={f} value={f}>{f}{longFormOnlyFields.includes(f) ? ' (long_form only)' : ''}</option>
                          ))}
                          {syntheticFields.length > 0 && (
                            <optgroup label="Computed by us">
                              {syntheticFields.map(f => (
                                <option key={f.field} value={f.field}>{f.label}</option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        )}

                        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>→</span>

                        <input
                          className="input"
                          style={{ flex: 1, minWidth: 140, height: 32, fontSize: 13 }}
                          placeholder="Buyer Target Field (e.g. firstName)"
                          value={m.target}
                          onChange={e => updateRow('field_mappings', idx, 'target', e.target.value)}
                        />

                        <select
                          className="select"
                          style={{ minWidth: 150, height: 32, fontSize: 13 }}
                          value={m.ruleType}
                          onChange={e => updateRow('field_mappings', idx, 'ruleType', e.target.value)}
                        >
                          <option value="simple">Simple Rename</option>
                          <option value="value_map">Value Map</option>
                          <option value="buckets">Numeric Buckets</option>
                          <option value="date_format">Date Format</option>
                          <option value="now">Current Timestamp</option>
                          <option value="concat">Concatenate Fields</option>
                          <option value="pay_cycle_offset">Pay Cycle Offset</option>
                        </select>

                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          style={{ padding: 4, height: 28, width: 28 }}
                          onClick={() => removeRow('field_mappings', idx)}
                        >
                          <Trash2 size={13} style={{ color: 'var(--red)' }} />
                        </button>
                      </div>

                      {m.ruleType === 'simple' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Send as:</label>
                            <TypeSelect
                              value={m.castType}
                              onChange={val => updateRow('field_mappings', idx, 'castType', val)}
                            />
                          </div>
                          {m.castType !== 'string' && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                              Forces the outgoing JSON type on this passthrough value, rather than sending whatever type happens to already be on the lead - e.g. a numeric field an affiliate might send as a numeric-looking string instead of a real JSON number.
                            </div>
                          )}
                        </div>
                      )}

                      {m.ruleType === 'value_map' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Default Fallback:</label>
                            <TypedValueInput
                              type={m.defaultType}
                              value={m.defaultValue}
                              onChange={val => updateRow('field_mappings', idx, 'defaultValue', val)}
                              placeholder="e.g. benefits, checking, no"
                              width={160}
                              height={28}
                            />
                            <TypeSelect
                              value={m.defaultType}
                              onChange={val => {
                                // Clears the paired value on type switch - otherwise a
                                // stale value (e.g. "employment" left over from String)
                                // survives under a Boolean/Number dropdown that visibly
                                // shows unset, while coerceTypedValue() silently turns it
                                // into a real false/0 at save time.
                                updateRow('field_mappings', idx, 'defaultType', val)
                                updateRow('field_mappings', idx, 'defaultValue', '')
                              }}
                            />
                            <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => addValueMapRow(idx)}>
                              + Value Pair
                            </button>
                          </div>

                          {m.valueMap && m.valueMap.length > 0 && (
                            <table className="mapping-table" style={{ marginTop: 4 }}>
                              <thead>
                                <tr>
                                  <th>From Value</th>
                                  <th>To Target Value</th>
                                  <th>Type</th>
                                  <th style={{ width: 30 }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {m.valueMap.map((vm, vIdx) => (
                                  <tr key={vIdx}>
                                    <td>
                                      <input
                                        className="input"
                                        style={{ height: 26, fontSize: 12 }}
                                        placeholder="e.g. employed"
                                        value={vm.from}
                                        onChange={e => updateValueMapRow(idx, vIdx, 'from', e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <TypedValueInput
                                        type={vm.toType}
                                        value={vm.to}
                                        onChange={val => updateValueMapRow(idx, vIdx, 'to', val)}
                                        placeholder="e.g. employment"
                                      />
                                    </td>
                                    <td>
                                      <TypeSelect
                                        value={vm.toType}
                                        onChange={val => {
                                          updateValueMapRow(idx, vIdx, 'toType', val)
                                          updateValueMapRow(idx, vIdx, 'to', '')
                                        }}
                                      />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-icon"
                                        style={{ padding: 2, height: 22, width: 22 }}
                                        onClick={() => removeValueMapRow(idx, vIdx)}
                                      >
                                        <Trash2 size={11} style={{ color: 'var(--red)' }} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}

                      {m.ruleType === 'buckets' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Default (above last bound):</label>
                            <TypedValueInput
                              type={m.defaultType}
                              value={m.defaultValue}
                              onChange={val => updateRow('field_mappings', idx, 'defaultValue', val)}
                              placeholder="e.g. More 3 years"
                              width={160}
                              height={28}
                            />
                            <TypeSelect
                              value={m.defaultType}
                              onChange={val => {
                                // Clears the paired value on type switch - otherwise a
                                // stale value (e.g. "employment" left over from String)
                                // survives under a Boolean/Number dropdown that visibly
                                // shows unset, while coerceTypedValue() silently turns it
                                // into a real false/0 at save time.
                                updateRow('field_mappings', idx, 'defaultType', val)
                                updateRow('field_mappings', idx, 'defaultValue', '')
                              }}
                            />
                            <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => addBucketRow(idx)}>
                              + Bucket
                            </button>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                            The first row whose "Upper Bound" the value is ≤ wins. A value above every bound falls to Default. Rows are sorted by Upper Bound automatically on save - order here doesn't matter.
                          </div>

                          {m.buckets && m.buckets.length > 0 && (
                            <table className="mapping-table" style={{ marginTop: 4 }}>
                              <thead>
                                <tr>
                                  <th>Upper Bound (≤)</th>
                                  <th>Label</th>
                                  <th style={{ width: 30 }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {m.buckets.map((b, bIdx) => (
                                  <tr key={bIdx}>
                                    <td>
                                      <input
                                        className="input"
                                        type="number"
                                        style={{ height: 26, fontSize: 12 }}
                                        placeholder="e.g. 11"
                                        value={b.upperBound}
                                        onChange={e => updateBucketRow(idx, bIdx, 'upperBound', e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        className="input"
                                        style={{ height: 26, fontSize: 12 }}
                                        placeholder="e.g. Less than 1 year"
                                        value={b.label}
                                        onChange={e => updateBucketRow(idx, bIdx, 'label', e.target.value)}
                                      />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-icon"
                                        style={{ padding: 2, height: 22, width: 22 }}
                                        onClick={() => removeBucketRow(idx, bIdx)}
                                      >
                                        <Trash2 size={11} style={{ color: 'var(--red)' }} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}

                      {m.ruleType === 'date_format' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Output Format (strftime):</label>
                          <input
                            className="input"
                            style={{ width: 160, height: 28, fontSize: 12 }}
                            placeholder="e.g. %m/%d/%Y"
                            value={m.dateFormat}
                            onChange={e => updateRow('field_mappings', idx, 'dateFormat', e.target.value)}
                          />
                          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Default (unparseable):</label>
                          <TypedValueInput
                            type={m.defaultType}
                            value={m.defaultValue}
                            onChange={val => updateRow('field_mappings', idx, 'defaultValue', val)}
                            placeholder="optional"
                            width={140}
                            height={28}
                          />
                          <TypeSelect
                            value={m.defaultType}
                            onChange={val => updateRow('field_mappings', idx, 'defaultType', val)}
                          />
                        </div>
                      )}

                      {m.ruleType === 'now' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Output Format (strftime):</label>
                          <input
                            className="input"
                            style={{ width: 160, height: 28, fontSize: 12 }}
                            placeholder="e.g. %m/%d/%Y %H:%M:%S"
                            value={m.dateFormat}
                            onChange={e => updateRow('field_mappings', idx, 'dateFormat', e.target.value)}
                          />
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            Stamps the current UTC time when the request is sent - doesn't read the Internal Field above, which is just a label.
                          </span>
                        </div>
                      )}

                      {m.ruleType === 'concat' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                            Joins the source fields below, in order, into one string for the Buyer Target Field above. Missing/empty source values are skipped (no stray separator). Use <code>__affiliate_id</code> / <code>__click_subid</code> / <code>__lead_id</code> to reference our affiliate id, the click-attribution subid, or the lead's own id, or a real lead field name. Tip: if a buyer wants the same lead id in two target fields (e.g. EPCVIP's applicant_id/application_id), route both through Concatenate Fields with a single <code>__lead_id</code> source - mixing a plain rename with concat can send one as a number and the other as a string.
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Separator:</label>
                            <input
                              className="input"
                              style={{ width: 80, height: 28, fontSize: 12 }}
                              placeholder="e.g. _"
                              value={m.separator}
                              onChange={e => updateRow('field_mappings', idx, 'separator', e.target.value)}
                            />
                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Default (all fields missing):</label>
                            <TypedValueInput
                              type={m.defaultType}
                              value={m.defaultValue}
                              onChange={val => updateRow('field_mappings', idx, 'defaultValue', val)}
                              placeholder="optional"
                              width={140}
                              height={28}
                            />
                            <TypeSelect
                              value={m.defaultType}
                              onChange={val => {
                                // Clears the paired value on type switch - otherwise a
                                // stale value (e.g. "employment" left over from String)
                                // survives under a Boolean/Number dropdown that visibly
                                // shows unset, while coerceTypedValue() silently turns it
                                // into a real false/0 at save time.
                                updateRow('field_mappings', idx, 'defaultType', val)
                                updateRow('field_mappings', idx, 'defaultValue', '')
                              }}
                            />
                            <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => addConcatFieldRow(idx)}>
                              + Source Field
                            </button>
                          </div>

                          {m.concatFields && m.concatFields.length > 0 && (
                            <table className="mapping-table" style={{ marginTop: 4 }}>
                              <thead>
                                <tr>
                                  <th>Source Field (in join order)</th>
                                  <th style={{ width: 30 }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {m.concatFields.map((f, fIdx) => (
                                  <tr key={fIdx}>
                                    <td>
                                      <input
                                        className="input"
                                        style={{ height: 26, fontSize: 12 }}
                                        placeholder="e.g. __affiliate_id"
                                        value={f}
                                        onChange={e => updateConcatFieldRow(idx, fIdx, e.target.value)}
                                      />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-icon"
                                        style={{ padding: 2, height: 22, width: 22 }}
                                        onClick={() => removeConcatFieldRow(idx, fIdx)}
                                      >
                                        <Trash2 size={11} style={{ color: 'var(--red)' }} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}

                      {m.ruleType === 'pay_cycle_offset' && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                            Computes a date offset from a base date by N pay cycles (Cycles below, defaults to 1 - the next pay date) - e.g. TruePath's next_pay_date2, or EPCVIP's pay_after_next_date (Cycles: 2). If Pay Frequency Field is missing/unrecognized on a lead, the offset defaults to biweekly (14 days). If Base Date Field is missing/unparseable, this field is omitted from the request entirely rather than sent blank.
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Base Date Field:</label>
                            <select
                              className="select"
                              style={{ minWidth: 140, height: 28, fontSize: 12 }}
                              value={m.baseDateField}
                              onChange={e => updateRow('field_mappings', idx, 'baseDateField', e.target.value)}
                            >
                              <option value="">-- Select Field --</option>
                              {m.baseDateField && !internalFields.includes(m.baseDateField) && (
                                <option value={m.baseDateField}>{m.baseDateField} (not in {form.vertical} schema)</option>
                              )}
                              {internalFields.map(f => (
                                <option key={f} value={f}>{f}</option>
                              ))}
                            </select>

                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Pay Frequency Field:</label>
                            <input
                              className="input"
                              style={{ width: 140, height: 28, fontSize: 12 }}
                              placeholder="pay_frequency"
                              value={m.payFrequencyField}
                              onChange={e => updateRow('field_mappings', idx, 'payFrequencyField', e.target.value)}
                            />

                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Output Format (strftime):</label>
                            <input
                              className="input"
                              style={{ width: 140, height: 28, fontSize: 12 }}
                              placeholder="e.g. %Y-%m-%d"
                              value={m.dateFormat}
                              onChange={e => updateRow('field_mappings', idx, 'dateFormat', e.target.value)}
                            />

                            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Cycles:</label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              className="input"
                              style={{ width: 70, height: 28, fontSize: 12 }}
                              placeholder="1"
                              value={m.cycles}
                              onChange={e => updateRow('field_mappings', idx, 'cycles', e.target.value)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}

          {step === 3 && (
            <div className="fade-in">
              <section className="partner-modal-section">
                <div className="partner-modal-section-head">
                  <h3>Step 3: Buyer Set Assignment</h3>
                  <p>
                    Assign this buyer to routing sets (waterfall, ping/post, direct). Only sets
                    matching this buyer's Engine ({ENGINE_LABELS[form.engine]}, from Step 1) are shown.
                  </p>
                </div>

                {buyerSets.length === 0 ? (
                  <div className="card partner-empty-card" style={{ padding: 20, textAlign: 'center' }}>
                    No buyer sets created yet. You can create Buyer Sets under <strong>/buyer-sets</strong>.
                  </div>
                ) : eligibleBuyerSets.length === 0 ? (
                  <div className="card partner-empty-card" style={{ padding: 20, textAlign: 'center' }}>
                    No buyer sets configured for the {ENGINE_LABELS[form.engine]} engine yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {eligibleBuyerSets.map(bs => {
                      const bsId = bs.id
                      const isAssigned = form.assigned_buyer_set_ids.includes(bsId)
                      // Excludes this buyer from the count - it's the one
                      // being edited, so its own existing membership on a
                      // direct set shouldn't count against that set's
                      // capacity (otherwise unchecking then rechecking it
                      // in the same sitting would lock the checkbox).
                      const otherMembers = (bs.buyers?.map(b => b.id || b) ?? bs.buyer_ids ?? []).filter(id => id !== buyerId)
                      const atDirectCapacity = bs.engine === 'direct' && otherMembers.length >= 1 && !isAssigned
                      return (
                        <label
                          key={bsId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            background: isAssigned ? 'var(--accent-soft)' : 'var(--bg-raised)',
                            border: `1px solid ${isAssigned ? 'var(--accent)' : 'var(--border)'}`,
                            borderRadius: 'var(--radius)',
                            cursor: atDirectCapacity ? 'not-allowed' : 'pointer',
                            opacity: atDirectCapacity ? 0.6 : 1
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <input
                              type="checkbox"
                              checked={isAssigned}
                              disabled={atDirectCapacity}
                              onChange={e => {
                                if (e.target.checked) {
                                  updateField('assigned_buyer_set_ids', [...form.assigned_buyer_set_ids, bsId])
                                } else {
                                  updateField('assigned_buyer_set_ids', form.assigned_buyer_set_ids.filter(id => id !== bsId))
                                }
                              }}
                            />
                            <div>
                              <strong style={{ fontSize: 14 }}>{bs.name}</strong>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                Vertical: {bs.vertical || 'loans'} | Engine: <span className="badge badge-purple" style={{ textTransform: 'uppercase', fontSize: 10 }}>{bs.engine || 'waterfall'}</span>
                                {atDirectCapacity ? ' | Already has a buyer - Direct Post sets hold only one' : ''}
                              </div>
                            </div>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                )}
              </section>
            </div>
          )}

          {error ? <div className="partner-modal-error" style={{ marginTop: 16 }}>{error}</div> : null}

          <div className="partner-modal-actions" style={{ marginTop: 24 }}>
            {step > 1 ? (
              <button type="button" className="btn btn-ghost" onClick={handlePrev} disabled={saving}>
                <ArrowLeft size={14} style={{ marginRight: 6 }} /> Previous
              </button>
            ) : (
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            )}

            {step < 3 ? (
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Next <ArrowRight size={14} style={{ marginLeft: 6 }} />
              </button>
            ) : (
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Complete Onboarding'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

// Best-effort sample values for the request-shape preview below, keyed by
// common internal field names. Anything not listed here just renders as
// <field_name> - the point is to see the target keys/headers/URL a buyer's
// saved config actually produces, not to simulate a real lead.
// Formats a preview-request API leg (request/post_confirm) into the props
// LegPreview expects, rendering the body per that leg's request_format.
function formatLegPreview(leg) {
  const body = leg.body || {}
  const bodyText = leg.request_format === 'form'
    ? new URLSearchParams(Object.entries(body).map(([k, v]) => [k, String(v)])).toString()
    : JSON.stringify(body, null, 2)

  return { url: leg.url || '(no URL configured)', method: leg.method || 'POST', headers: leg.headers || {}, bodyText }
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      style={{ padding: '2px 8px', height: 24, fontSize: 11 }}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
          setCopied(true)
          setTimeout(() => setCopied(false), 1200)
        } catch { /* clipboard unavailable, ignore */ }
      }}
    >
      <Copy size={11} style={{ marginRight: 4 }} /> {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

function LegPreview({ title, note, preview }) {
  const headerEntries = Object.entries(preview.headers)
  const headerText = headerEntries.map(([k, v]) => `${k}: ${v}`).join('\n')
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <h4 style={{ margin: 0 }}>{title}</h4>
        {note ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{note}</span> : null}
      </div>

      <div style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, overflowX: 'auto' }}>
        <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{preview.method}</span> {preview.url}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Headers</span>
        {headerEntries.length > 0 ? <CopyButton text={headerText} /> : null}
      </div>
      <pre style={{ fontSize: 12, background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10, marginTop: 0, marginBottom: 8, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
        {headerEntries.length > 0 ? headerText : '(no custom headers configured)'}
      </pre>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Body</span>
        <CopyButton text={preview.bodyText} />
      </div>
      <pre style={{ fontSize: 12, background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10, marginTop: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
        {preview.bodyText}
      </pre>
    </div>
  )
}

function RequestPreviewModal({ buyer, onClose }) {
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let ignore = false
    setLoading(true)
    api.get(`/buyers/${buyer.id}/preview-request`).then(res => {
      if (!ignore) setPreview(res.data)
    }).catch(err => {
      if (!ignore) setError(err.response?.data?.detail || 'Error loading buyer endpoint config.')
    }).finally(() => {
      if (!ignore) setLoading(false)
    })
    return () => { ignore = true }
  }, [buyer.id])

  const isPingPost = !!preview?.post_confirm
  const primaryLeg = preview ? formatLegPreview(preview.request) : null
  const postLeg = preview?.post_confirm ? formatLegPreview(preview.post_confirm) : null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal partner-modal partner-modal-wide" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="partner-modal-header" style={{ paddingBottom: 12 }}>
          <div>
            <h2>Request Preview — {buyer.name}</h2>
            <p>
              Server-side dry run built from this buyer's saved endpoint config. URL, method,
              headers, and mapped target keys are exact; lead-dependent values are sample
              placeholders (not a real lead, no network call) so you can check the request shape
              without sending anything.
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="partner-modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: 24 }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading endpoint config…</div>
          ) : error ? (
            <div className="alert alert-error">{error}</div>
          ) : (
            <>
              <LegPreview
                title={isPingPost ? 'Ping Leg' : 'Request'}
                note={`${preview.request.request_format || 'json'} body`}
                preview={primaryLeg}
              />
              {postLeg ? (
                <LegPreview
                  title="Post/Confirm Leg"
                  note={`sent once a bid_id is captured from the ping leg · ${preview.post_confirm.request_format || 'json'} body`}
                  preview={postLeg}
                />
              ) : null}

              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Expected response fields (JSON path)
                </span>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, fontFamily: 'monospace' }}>
                  {Object.entries(preview.response_mapping || {}).map(([field, path]) => (
                    <li key={field}>{field} → {Array.isArray(path) ? (path.join(', ') || '(unset)') : (path || '(unset)')}</li>
                  ))}
                </ul>
                {postLeg ? (
                  <>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginTop: 10 }}>
                      Expected post/confirm response fields (JSON path)
                    </span>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, fontFamily: 'monospace' }}>
                      {Object.entries(preview.post_response_mapping || {}).map(([field, path]) => (
                        <li key={field}>{field} → {path || '(unset)'}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function BuyerCard({ buyer, canManageBuyers, onEdit, onDelete, onPreview }) {
  const isActive = buyer.is_active
  const endpointActive = buyer.endpoint?.is_active !== false
  const isRoutable = isActive && endpointActive
  const statusCls = isRoutable ? 'badge-green' : isActive ? 'badge-amber' : 'badge-red'
  const statusStr = isRoutable ? 'Active' : isActive ? 'Endpoint Inactive' : 'Inactive'

  return (
    <div className="card fade-in partners-card">
      <div className="partners-card-head">
        <div className="partners-card-title-wrap">
          <div className="partners-card-title">{buyer.name}</div>
          <div className="partners-card-subtitle">
            Company ID: {buyer.company_id} | Min Payout: {buyer.minimum_lead_payout ? `$${buyer.minimum_lead_payout}` : 'None'}
          </div>
        </div>
        <div className="partners-card-badges">
          <span className={`badge ${statusCls}`}>{statusStr}</span>
          <span className="badge badge-purple">{ENGINE_LABELS[buyer.engine] || buyer.engine || ENGINE_LABELS.waterfall}</span>
          {buyer.requires_tier_map ? <span className="badge badge-purple">Requires Tier Map</span> : null}
        </div>
      </div>

      <div style={{ padding: '12px 16px', fontSize: 12, borderTop: '1px solid var(--border)', background: 'var(--bg-raised)', borderRadius: '0 0 var(--radius) var(--radius)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, color: 'var(--text-secondary)' }}>
          <Webhook size={13} style={{ color: 'var(--accent)' }} />
          <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {buyer.endpoint?.url || buyer.endpoint_url || 'No Webhook Configured'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}>
          <Layers3 size={13} />
          <span>
            {buyer.buyer_set_names?.length > 0 
              ? `Buyer Sets: ${buyer.buyer_set_names.join(', ')}`
              : buyer.buyer_set_ids?.length > 0 
              ? `${buyer.buyer_set_ids.length} Buyer Set(s) assigned`
              : 'Unassigned'}
          </span>
        </div>
      </div>

      <div className="partners-card-actions" style={{ display: 'flex', gap: 8 }}>
        {canManageBuyers ? (
          <>
            <button className="btn btn-ghost partners-card-btn" style={{ flex: 1 }} onClick={() => onEdit(buyer)}>
              <Pencil size={13} style={{ marginRight: 4 }} /> Edit
            </button>
            <button className="btn btn-ghost partners-card-btn" style={{ flex: 1 }} onClick={() => onPreview(buyer)}>
              <Eye size={13} style={{ marginRight: 4 }} /> Preview Request
            </button>
            <button className="btn btn-ghost partners-card-btn" style={{ color: 'var(--red)' }} onClick={() => onDelete(buyer.id)}>
              <Trash2 size={13} />
            </button>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default function BuyersPage() {
  const { user, isAdmin } = useAuth()
  const canManageBuyers = isAdmin || user?.is_staff

  const [buyers, setBuyers] = useState([])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [total, setTotal] = useState(0)
  const [editorFor, setEditorFor] = useState(null)
  const [previewFor, setPreviewFor] = useState(null)

  const load = useCallback(async (search = '', compId = '') => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      params.set('limit', '50')
      params.set('page_size', '50')
      params.set('per_page', '50')
      if (search) params.set('search', search)
      if (compId) params.set('company_id', compId)
      const res = await api.get(`/buyers/?${params.toString()}`)
      
      const items = res.data?.items || res.data || []
      setBuyers(items)
      setTotal(res.data?.count || items.length || 0)
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading buyers.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    api.get('/companies/').then(res => {
      setCompanies(res.data?.items || res.data || [])
    }).catch(() => {})
    load(q, companyFilter)
  }, [load])

  const handleDelete = async (buyerId) => {
    if (!window.confirm('Are you sure you want to delete this buyer profile?')) return
    try {
      await api.delete(`/buyers/${buyerId}`)
      toast.success('Buyer deleted')
      load(q, companyFilter)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting buyer')
    }
  }

  return (
    <div className="fade-in page-shell partners-page-shell">
      <style>{BUYERS_RESPONSIVE_CSS}</style>

      <section className="page-hero partners-hero">
        <div className="page-hero-content">
          <span className="hero-kicker">Buyer Management</span>
          <h1>Onboard Buyers & Configure Outbound Webhooks.</h1>
          <p>
            Manage buyer profiles, minimum lead payout requirements, webhook endpoints,
            and route assignments for waterfall and auction engines.
          </p>
          <div className="hero-chips partners-header-badges">
            <span className="badge badge-blue">Routing Panel</span>
            {canManageBuyers
              ? <span className="badge badge-green">Full Management</span>
              : <span className="badge badge-amber">Read-Only</span>}
          </div>
        </div>
        <div className="page-hero-actions partners-toolbar-actions">
          <button className="btn btn-ghost" onClick={() => load(q, companyFilter)}>Refresh</button>
          {canManageBuyers ? (
            <button className="btn btn-primary partners-create-btn" onClick={() => setEditorFor('create')}>
              + Onboard Buyer
            </button>
          ) : null}
        </div>
      </section>

      {!loading ? (
        <div className="partners-stats-grid">
          <div className="card partners-stat-card">
            <div className="partners-stat-value">{total}</div>
            <div className="partners-stat-label">Total Buyers</div>
          </div>
          <div className="card partners-stat-card">
            <div className="partners-stat-value">{buyers.filter(b => b.is_active !== false).length}</div>
            <div className="partners-stat-label">Active Buyers</div>
          </div>
        </div>
      ) : null}

      <section className="card partners-filters partners-filters-panel" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div className="search-bar search-bar-elevated" style={{ flex: 1, minWidth: 200 }}>
          <span className="search-icon"><Search size={14} /></span>
          <input
            className="input"
            placeholder="Search by buyer name…"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && load(q, companyFilter)}
          />
        </div>

        <select
          className="select"
          style={{ minWidth: 180 }}
          value={companyFilter}
          onChange={e => {
            setCompanyFilter(e.target.value)
            load(q, e.target.value)
          }}
        >
          <option value="">All Companies</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <button className="btn btn-primary" onClick={() => load(q, companyFilter)}>Search</button>

        {canManageBuyers ? (
          <button className="btn btn-primary partners-create-btn" onClick={() => setEditorFor('create')}>
            <Plus size={14} style={{ marginRight: 6 }} /> + Onboard Buyer
          </button>
        ) : null}
      </section>

      {loading ? <div className="partner-empty-block">Loading buyers…</div> : null}
      {error ? <div className="partner-modal-error partner-page-error">{error}</div> : null}

      {!loading && !error && buyers.length === 0 ? (
        <div className="card partner-empty-card">
          No buyers found matching your criteria.
          <br />
          <span>
            {canManageBuyers
              ? 'You can onboard a new buyer using the "+ Onboard Buyer" button.'
              : 'Contact an administrator to onboard buyers.'}
          </span>
        </div>
      ) : null}

      {!loading && buyers.length > 0 ? (
        <div className="partners-grid">
          {buyers.map((item) => (
            <BuyerCard
              key={item.id}
              buyer={item}
              canManageBuyers={canManageBuyers}
              onEdit={(item) => setEditorFor(item)}
              onDelete={handleDelete}
              onPreview={(item) => setPreviewFor(item)}
            />
          ))}
        </div>
      ) : null}

      {editorFor ? (
        <BuyerOnboardingWizardModal
          buyer={editorFor === 'create' ? null : editorFor}
          canManageBuyers={canManageBuyers}
          onClose={() => setEditorFor(null)}
          onSaved={() => load(q, companyFilter)}
        />
      ) : null}

      {previewFor ? (
        <RequestPreviewModal buyer={previewFor} onClose={() => setPreviewFor(null)} />
      ) : null}
    </div>
  )
}
